import { Link } from "react-router-dom";
import { ArrowRight, Music2, Palette, Eye, Image as ImageIcon, Globe2, BadgeCheck, Sparkles, Check } from "lucide-react";
import { Logo } from "../components/Logo";
import { useAuth } from "../context/AuthContext";
import { ProfileView } from "../components/ProfileView";

const DEMO = {
  username: "nyx", display_name: "nyx", bio: "digital ghost · making noise after midnight", location: "berlin", layout: "classic",
  accent_color: "#ff3b91", avatar_url: "https://images.unsplash.com/photo-1633218388467-539651dcf81f?w=300&q=70", background_url: "", music_url: "",
  bg_dim: 0, bg_blur: 0, card_opacity: 55, glow: true, show_views: true, avatar_shape: "circle", verified: true, views: 18342,
  badges: [{ id: "premium", name: "Premium", icon: "gem", color: "#c01867" }, { id: "b1", name: "Early", icon: "flame", color: "#ff7ab8" }, { id: "b2", name: "Dev", icon: "code", color: "#ffffff" }],
  links: [{ id: "1", platform: "discord", url: "https://discord.com" }, { id: "2", platform: "spotify", url: "https://spotify.com" }, { id: "3", platform: "github", url: "https://github.com" }, { id: "4", platform: "tiktok", url: "https://tiktok.com" }],
};

const FEATURES = [
  { icon: Palette, t: "Layouts that bend", d: "Four layouts, accent colours, glow, blur, dim and typewriter bios." },
  { icon: Music2, t: "A soundtrack", d: "Drop an mp3 and your page plays it the moment someone walks in." },
  { icon: Eye, t: "Real view counts", d: "Unique visitors, counted once a day, no bots padding the number." },
  { icon: ImageIcon, t: "Image hosting", d: "Upload, share and serve images from your own alias domain." },
  { icon: Globe2, t: "Alias domains", d: "Swap visc.lol for any domain in our pool. Same page, new address." },
  { icon: BadgeCheck, t: "Badges & checks", d: "Verified checkmarks, staff badges and a badge you design yourself." },
];

const PRICES = [
  { id: "premium", n: "Premium", p: "6.99", d: "lifetime", perks: ["All 4 layouts", "Profile music", "Custom accent colour", "Typewriter bio", "25 links"], hot: true },
  { id: "image_host", n: "Image Hosting", p: "3.99", d: "lifetime", perks: ["Unlimited uploads", "Share pages", "Alias domain links"] },
  { id: "custom_domain", n: "Custom Domain", p: "2.99", d: "one-time", perks: ["Pick any alias domain", "Shown on your profile link"] },
  { id: "verified", n: "Verified", p: "3.99", d: "one-time", perks: ["Blue checkmark", "Next to your name, everywhere"] },
  { id: "custom_badge", n: "Custom Badge", p: "4.99", d: "one-time", perks: ["Your name, icon & colour", "Displayed on your profile"] },
];

const Nav = () => {
  const { user } = useAuth();
  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/5 bg-[#080808]/70 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 md:px-8">
        <Logo />
        <nav className="hidden items-center gap-8 text-sm text-zinc-400 md:flex">
          <a href="#features" className="transition-colors hover:text-white" data-testid="nav-features-link">features</a>
          <a href="#pricing" className="transition-colors hover:text-white" data-testid="nav-pricing-link">pricing</a>
        </nav>
        <div className="flex items-center gap-2">
          {user ? <Link to="/dashboard" className="btn-pink" data-testid="nav-dashboard-button">dashboard</Link> : (<>
            <Link to="/login" className="px-3 py-2 text-sm text-zinc-300 transition-colors hover:text-white" data-testid="nav-login-link">log in</Link>
            <Link to="/register" className="btn-pink" data-testid="nav-register-button">claim yours</Link>
          </>)}
        </div>
      </div>
    </header>
  );
};

export default function Landing() {
  return (
    <div className="relative min-h-screen overflow-hidden bg-[#080808] text-white">
      <Nav />
      <div className="pointer-events-none absolute -top-40 left-1/2 h-[520px] w-[900px] -translate-x-1/2 rounded-full bg-[#ff3b91]/15 blur-[140px]" />
      <section className="relative mx-auto grid max-w-7xl items-center gap-16 px-5 pb-24 pt-36 md:px-8 lg:grid-cols-[1.15fr_1fr] lg:pt-44">
        <div className="min-w-0 animate-fade-up">
          <div className="eyebrow mb-6 flex items-center gap-2"><Sparkles className="h-3.5 w-3.5 text-[#ff3b91]" /> your corner of the internet</div>
          <h1 className="font-display text-[2.6rem] font-extrabold leading-[0.95] tracking-tight sm:text-6xl lg:text-7xl">
            one link.<br /><span className="grad-text">all of you.</span>
          </h1>
          <p className="mt-7 max-w-lg text-base text-zinc-400 md:text-lg">Build a profile that actually feels like you — music, badges, glowing layouts and your own domain. Free to start, lifetime upgrades.</p>
          <ClaimBar />
          <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-xs text-zinc-500">
            <span><span className="text-white">€0</span> to start</span><span className="h-3 w-px bg-zinc-800" /><span><span className="text-white">no</span> subscriptions</span><span className="h-3 w-px bg-zinc-800" /><span><span className="text-white">4</span> layouts</span>
          </div>
        </div>
        <div className="relative flex min-w-0 justify-center animate-floaty">
          <div className="absolute inset-0 -z-0 rounded-[40px] bg-gradient-to-br from-[#ff3b91]/20 via-transparent to-[#800d43]/30 blur-2xl" />
          <div className="noise relative w-full max-w-md overflow-hidden rounded-[32px] border border-white/10 bg-[#0b0b0e] p-6 sm:p-8">
            <div className="absolute -left-10 top-10 h-60 w-60 rounded-full bg-[#ff3b91] opacity-25 blur-[90px]" />
            <div className="mb-5 flex items-center justify-between font-mono text-[10px] text-zinc-500"><span>visc.lol/nyx</span><span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-[#ff3b91]" />live</span></div>
            <ProfileView profile={DEMO} />
          </div>
        </div>
      </section>

      <section id="features" className="relative mx-auto max-w-7xl px-5 py-24 md:px-8">
        <div className="eyebrow mb-4">what you get</div>
        <h2 className="max-w-xl font-display text-3xl font-bold tracking-tight sm:text-4xl">everything a profile should've been.</h2>
        <div className="mt-14 grid gap-px overflow-hidden rounded-3xl border border-[#1f1f28] bg-[#1f1f28] sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f, i) => (
            <div key={f.t} data-testid={`feature-card-${i}`} className="group bg-[#0a0a0c] p-8 transition-colors duration-300 hover:bg-[#0f0f13]">
              <f.icon className="h-6 w-6 text-[#ff3b91] transition-transform duration-300 group-hover:-translate-y-1" />
              <h3 className="mt-6 font-display text-lg font-semibold">{f.t}</h3>
              <p className="mt-2 text-sm text-zinc-400">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="pricing" className="relative mx-auto max-w-7xl px-5 py-24 md:px-8">
        <div className="eyebrow mb-4">pricing</div>
        <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">pay once. keep it forever.</h2>
        <p className="mt-3 text-zinc-400">No subscriptions. Every upgrade is a one-time purchase.</p>
        <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-5">
          {PRICES.map((p) => (
            <div key={p.id} data-testid={`pricing-card-${p.id}`} className={`relative flex flex-col rounded-2xl border p-6 transition-all duration-300 hover:-translate-y-1 ${p.hot ? "border-[#ff3b91]/60 bg-gradient-to-b from-[#ff3b91]/10 to-[#0d0d10] shadow-[0_0_40px_rgba(255,59,145,.15)] lg:col-span-1" : "border-[#1f1f28] bg-[#0d0d10] hover:border-[#ff3b91]/40"}`}>
              {p.hot && <span className="absolute -top-2.5 left-6 rounded-full bg-[#ff3b91] px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-wider">popular</span>}
              <div className="font-display text-lg font-semibold">{p.n}</div>
              <div className="mt-4 flex items-baseline gap-1"><span className="font-display text-4xl font-bold">€{p.p}</span><span className="text-xs text-zinc-500">{p.d}</span></div>
              <ul className="mt-6 flex-1 space-y-2.5 text-sm text-zinc-400">
                {p.perks.map((k) => <li key={k} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[#ff3b91]" />{k}</li>)}
              </ul>
              <Link to="/dashboard/store" data-testid={`pricing-buy-${p.id}`} className={`mt-6 ${p.hot ? "btn-pink" : "btn-ghost"}`}>get it</Link>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-24 md:px-8">
        <div className="noise relative overflow-hidden rounded-3xl border border-[#ff3b91]/25 bg-[#0d0d10] px-8 py-16 md:px-16">
          <div className="absolute -right-20 -top-20 h-80 w-80 rounded-full bg-[#ff3b91]/25 blur-[100px]" />
          <h2 className="relative max-w-xl font-display text-3xl font-bold tracking-tight sm:text-5xl">your name is still free. <span className="grad-text">probably.</span></h2>
          <Link to="/register" data-testid="cta-register-button" className="btn-pink relative mt-8">claim it now <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </section>

      <footer className="border-t border-white/5">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-4 px-5 py-10 text-sm text-zinc-500 md:flex-row md:items-center md:px-8">
          <Logo />
          <span className="font-mono text-xs">© {new Date().getFullYear()} visc.lol — made after dark.</span>
        </div>
      </footer>
    </div>
  );
}

const ClaimBar = () => {
  const go = (e) => {
    e.preventDefault();
    const u = new FormData(e.target).get("u") || "";
    window.location.href = `/register?u=${encodeURIComponent(u.toString().toLowerCase())}`;
  };
  return (
    <form onSubmit={go} className="mt-10 flex w-full max-w-md items-center gap-2 rounded-2xl border border-[#262630] bg-[#0d0d10] p-1.5 pl-4 transition-colors focus-within:border-[#ff3b91]/60">
      <span className="font-mono text-sm text-zinc-500">visc.lol/</span>
      <input name="u" data-testid="hero-claim-input" placeholder="you" maxLength={20} className="min-w-0 flex-1 bg-transparent font-mono text-sm text-white outline-none placeholder:text-zinc-700" />
      <button data-testid="hero-claim-button" className="btn-pink">claim <ArrowRight className="h-4 w-4" /></button>
    </form>
  );
};
